// ============================================
// Sidebar Component — Role-based navigation
// ============================================

import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Settings, LineChart, ClipboardList,
  Inbox, Workflow, FolderOpen, Target, FileText, Zap, MessageSquare, BookOpen, Lightbulb, Gamepad2, Wrench, Briefcase,
  AlertTriangle, Eye, Archive
} from 'lucide-react';
import type { UserRole } from '../../types';

interface SidebarProps {
  role: UserRole;
  collapsed: boolean;
  onToggle: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: Record<UserRole, NavItem[]> = {
  architect: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'users', label: 'Users', icon: <Users size={20} /> },
    { id: 'teams', label: 'Teams', icon: <Building2 size={20} /> },
  ],
  operations_manager: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'teams', label: 'Teams', icon: <Building2 size={20} /> },
  ],
  team_leader: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'team-score', label: 'Team Score', icon: <Target size={20} /> },
    { id: 'monitor-escalations', label: 'Monitor Escalations', icon: <Eye size={20} /> },
  ],
  queue_manager: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'queues', label: 'Queues', icon: <Inbox size={20} /> },
  ],
  sme: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'escalations', label: 'Escalations', icon: <AlertTriangle size={20} /> },
    { id: 'see-discussions', label: 'SEE Discussions', icon: <MessageSquare size={20} /> },
    { id: 'escalation-archive', label: 'Escalation Archive', icon: <Archive size={20} /> },
  ],
  premier_engineer: [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'case-control', label: 'Case Control', icon: <Gamepad2 size={20} /> },
    { id: 'active-cases', label: 'Active Cases', icon: <FolderOpen size={20} /> },
    { id: 'target-metrics', label: 'Target Metrics', icon: <Target size={20} /> },
    { id: 'corrections', label: 'Corrections', icon: <Wrench size={20} /> },
    { id: 'team-manager-cards', label: 'TM Cards', icon: <Briefcase size={20} /> },
    { id: 'escalations', label: 'Escalations', icon: <AlertTriangle size={20} /> },
  ],
};

export default function Sidebar({ role, collapsed, onToggle, activeTab, onTabChange, onMouseEnter, onMouseLeave }: SidebarProps) {
  const items = NAV_ITEMS[role] || [];

  return (
    <aside 
      className={`glass-sidebar ${collapsed ? 'glass-sidebar--collapsed' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="glass-sidebar__brand">
        <div className="glass-sidebar__brand-icon">
          <Zap size={24} style={{ color: '#ff2a2a', filter: 'drop-shadow(0 0 8px rgba(255, 42, 42, 0.8))' }} />
        </div>
        <span className="glass-sidebar__brand-text">Premier Tracker</span>
      </div>

      <nav className="glass-sidebar__nav">
        {items.map((item) => (
          <button
            key={item.id}
            className={`glass-sidebar__item ${activeTab === item.id ? 'glass-sidebar__item--active' : ''}`}
            onClick={() => onTabChange?.(item.id)}
            title={collapsed ? item.label : undefined}
          >
            <span className="glass-sidebar__item-icon">{item.icon}</span>
            <span className="glass-sidebar__item-text">{item.label}</span>
          </button>
        ))}
      </nav>

      <div style={{ padding: 'var(--space-3) var(--space-2)', flexShrink: 0 }}>
        <button
          className="glass-sidebar__item glass-sidebar__collapse-btn"
          onClick={onToggle}
          style={{ width: '100%' }}
        >
          <span className="glass-sidebar__item-icon">{collapsed ? '→' : '←'}</span>
          <span className="glass-sidebar__item-text">Collapse</span>
        </button>
      </div>
    </aside>
  );
}
